/**
 * Element Inspector Utility
 * Press Alt+X+C to activate inspector mode
 * Hover over elements to highlight them with layered overlays:
 *   - BLUE overlay = content area (innermost - actual content)
 *   - ORANGE overlay = padding area (middle - space inside element)
 *   - RED overlay = margin area (outermost - space outside element)
 * Click to copy element selector to clipboard
 */

export class ElementInspector {
  private isActive = false;
  private overlay: HTMLDivElement | null = null;
  private spacingOverlay: HTMLDivElement | null = null; // RED overlay for margins
  private paddingOverlay: HTMLDivElement | null = null; // ORANGE overlay for padding
  private label: HTMLDivElement | null = null;
  private keySequence: string[] = [];
  private sequenceTimeout: number | null = null;
  private currentTarget: HTMLElement | null = null;
  private currentParentIndex = 0;
  private parentChain: HTMLElement[] = [];

  constructor() {
    this.init();
  }

  private init() {
    // Listen for keyboard shortcuts
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  private handleKeyDown(e: KeyboardEvent) {
    // Track Alt+X+C sequence
    if (e.altKey && e.key.toLowerCase() === 'x') {
      this.keySequence = ['x'];
      e.preventDefault();

      if (this.sequenceTimeout) clearTimeout(this.sequenceTimeout);
      this.sequenceTimeout = window.setTimeout(() => {
        this.keySequence = [];
      }, 1000);
      return;
    }

    if (this.keySequence.length === 1 && this.keySequence[0] === 'x' && e.key.toLowerCase() === 'c') {
      this.keySequence.push('c');
      e.preventDefault();
      this.toggleInspector();
      this.keySequence = [];
      if (this.sequenceTimeout) clearTimeout(this.sequenceTimeout);
      return;
    }

    // Press Escape to deactivate
    if (e.key === 'Escape' && this.isActive) {
      e.preventDefault();
      this.deactivate();
      return;
    }

    // Navigate parent chain with + and - keys when inspector is active
    if (this.isActive && this.parentChain.length > 0) {
      // '+' or '=' key - go deeper (smaller element)
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        if (this.currentParentIndex > 0) {
          this.currentParentIndex--;
          const element = this.parentChain[this.currentParentIndex];
          this.updateHighlight(element);
          const selector = this.generateSelector(element);
          this.showNotification(`↓ أصغر - المستوى ${this.currentParentIndex + 1}/${this.parentChain.length}: ${selector}`, false, 1500);
        }
        return;
      }

      // '-' key - go higher (bigger container)
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        if (this.currentParentIndex < this.parentChain.length - 1) {
          this.currentParentIndex++;
          const element = this.parentChain[this.currentParentIndex];
          this.updateHighlight(element);
          const selector = this.generateSelector(element);
          this.showNotification(`↑ أكبر - المستوى ${this.currentParentIndex + 1}/${this.parentChain.length}: ${selector}`, false, 1500);
        }
        return;
      }
    }
  }

  private toggleInspector() {
    if (this.isActive) {
      this.deactivate();
    } else {
      this.activate();
    }
  }

  private activate() {
    this.isActive = true;

    // Create margin overlay element (RED - shows margin area outside element)
    this.spacingOverlay = document.createElement('div');
    this.spacingOverlay.style.cssText = `
      position: fixed;
      pointer-events: none;
      border: 2px dashed oklch(0.6279 0.2476 29.2339);
      background: oklch(0.6279 0.2476 29.2339);
      opacity: 0.15;
      z-index: 999997;
      transition: all 0.1s ease;
    `;
    document.body.appendChild(this.spacingOverlay);

    // Create padding overlay element (ORANGE - shows padding area inside element)
    this.paddingOverlay = document.createElement('div');
    this.paddingOverlay.style.cssText = `
      position: fixed;
      pointer-events: none;
      border: 2px dashed oklch(0.7019 0.1683 70.0823);
      background: oklch(0.7019 0.1683 70.0823);
      opacity: 0.15;
      z-index: 999998;
      transition: all 0.1s ease;
    `;
    document.body.appendChild(this.paddingOverlay);

    // Create content overlay element (BLUE - shows actual content area)
    this.overlay = document.createElement('div');
    this.overlay.style.cssText = `
      position: fixed;
      pointer-events: none;
      border: 2px solid var(--primary-blue);
      background: var(--primary-blue);
      opacity: 0.1;
      z-index: 999999;
      transition: all 0.1s ease;
    `;
    document.body.appendChild(this.overlay);

    // Create label element
    this.label = document.createElement('div');
    this.label.style.cssText = `
      position: fixed;
      pointer-events: none;
      background: oklch(0.1448 0 0);
      color: oklch(1.0000 0 0);
      padding: 4px 8px;
      font-family: var(--font-mono);
      font-size: 12px;
      z-index: 1000000;
      border: 1px solid var(--primary-blue);
      white-space: nowrap;
      direction: ltr;
    `;
    document.body.appendChild(this.label);

    // Add event listeners
    document.addEventListener('mousemove', this.handleMouseMove.bind(this));
    document.addEventListener('click', this.handleClick.bind(this), true);

    // Show notification
    this.showNotification('وضع الفحص مفعل | أزرق=محتوى | برتقالي=حشو | أحمر=هامش | انقر لنسخ | Esc للإلغاء', false, 4000);
  }

  private deactivate(silent = false) {
    this.isActive = false;

    // Remove margin overlay
    if (this.spacingOverlay && this.spacingOverlay.parentNode) {
      this.spacingOverlay.parentNode.removeChild(this.spacingOverlay);
      this.spacingOverlay = null;
    }

    // Remove padding overlay
    if (this.paddingOverlay && this.paddingOverlay.parentNode) {
      this.paddingOverlay.parentNode.removeChild(this.paddingOverlay);
      this.paddingOverlay = null;
    }

    // Remove content overlay
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
      this.overlay = null;
    }

    // Remove label
    if (this.label && this.label.parentNode) {
      this.label.parentNode.removeChild(this.label);
      this.label = null;
    }

    // Remove event listeners
    document.removeEventListener('mousemove', this.handleMouseMove.bind(this));
    document.removeEventListener('click', this.handleClick.bind(this), true);

    // Only show deactivation message if not silent
    if (!silent) {
      this.showNotification('وضع الفحص متوقف');
    }
  }

  private handleMouseMove(e: MouseEvent) {
    if (!this.isActive || !this.overlay) return;

    // Temporarily hide overlay to get element underneath
    if (this.overlay) {
      this.overlay.style.display = 'none';
    }

    // Get the deepest element at mouse position, piercing through shadow DOM
    const target = this.getDeepestElementAtPoint(e.clientX, e.clientY);

    // Show overlay again
    if (this.overlay) {
      this.overlay.style.display = 'block';
    }

    console.log('Hovering over:', target, 'Tag:', target?.tagName, 'Rect:', target?.getBoundingClientRect());

    if (!target || target === document.body || target === document.documentElement) return;

    // Reset to smallest element when mouse moves
    this.currentTarget = target;
    this.currentParentIndex = 0;
    this.buildParentChain(target);

    this.updateHighlight(target);
  }

  private getDeepestElementAtPoint(x: number, y: number): HTMLElement {
    let element = document.elementFromPoint(x, y) as HTMLElement;

    // Keep drilling down through shadow roots
    while (element?.shadowRoot) {
      const innerElement = element.shadowRoot.elementFromPoint(x, y) as HTMLElement;
      if (!innerElement || innerElement === element) break;
      element = innerElement;
    }

    return element;
  }

  private buildParentChain(element: HTMLElement) {
    this.parentChain = [];
    let current: HTMLElement | null = element;

    while (current && current !== document.body) {
      this.parentChain.push(current);

      // Try to get parent element
      let parent: HTMLElement | null = current.parentElement;

      // If no parent element, check if we're in a shadow root
      if (!parent) {
        const parentNode = current.parentNode;
        if (parentNode && (parentNode as any).host) {
          // We're at the top of a shadow tree, jump to host
          current = (parentNode as ShadowRoot).host as HTMLElement;
          continue;
        }
      }

      current = parent;
    }
  }

  private updateHighlight(element: HTMLElement) {
    if (!this.overlay || !this.label || !this.spacingOverlay || !this.paddingOverlay) return;

    const rect = element.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(element);

    // Parse margin values
    const marginTop = parseFloat(computedStyle.marginTop) || 0;
    const marginRight = parseFloat(computedStyle.marginRight) || 0;
    const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
    const marginLeft = parseFloat(computedStyle.marginLeft) || 0;

    // Parse padding values
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;

    // Content area excluding padding (BLUE overlay - innermost)
    const contentTop = rect.top + paddingTop;
    const contentLeft = rect.left + paddingLeft;
    const contentWidth = rect.width - paddingLeft - paddingRight;
    const contentHeight = rect.height - paddingTop - paddingBottom;

    this.overlay.style.top = `${contentTop}px`;
    this.overlay.style.left = `${contentLeft}px`;
    this.overlay.style.width = `${contentWidth}px`;
    this.overlay.style.height = `${contentHeight}px`;

    // Padding area (ORANGE overlay - shows full element including padding)
    this.paddingOverlay.style.top = `${rect.top}px`;
    this.paddingOverlay.style.left = `${rect.left}px`;
    this.paddingOverlay.style.width = `${rect.width}px`;
    this.paddingOverlay.style.height = `${rect.height}px`;

    // Hide padding overlay if there's no padding
    const hasPadding = paddingTop > 0 || paddingRight > 0 || paddingBottom > 0 || paddingLeft > 0;
    this.paddingOverlay.style.display = hasPadding ? 'block' : 'none';

    // Margin area (RED overlay - outermost)
    const spacingTop = rect.top - marginTop;
    const spacingLeft = rect.left - marginLeft;
    const spacingWidth = rect.width + marginLeft + marginRight;
    const spacingHeight = rect.height + marginTop + marginBottom;

    this.spacingOverlay.style.top = `${spacingTop}px`;
    this.spacingOverlay.style.left = `${spacingLeft}px`;
    this.spacingOverlay.style.width = `${spacingWidth}px`;
    this.spacingOverlay.style.height = `${spacingHeight}px`;

    // Hide margin overlay if there's no margin
    const hasMargin = marginTop > 0 || marginRight > 0 || marginBottom > 0 || marginLeft > 0;
    this.spacingOverlay.style.display = hasMargin ? 'block' : 'none';

    // Update label with element selector
    const selector = this.generateSelector(element);
    const ariaLabel = element.getAttribute('aria-label');

    let labelText = selector;
    if (ariaLabel) {
      labelText = `${selector} [${ariaLabel}]`;
    }

    // Add dimensions (width x height)
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    labelText += ` | ${width}×${height}px`;

    // Add padding info if padding exists
    if (hasPadding) {
      const paddings = [];
      if (paddingTop > 0) paddings.push(`↑${paddingTop}px`);
      if (paddingRight > 0) paddings.push(`→${paddingRight}px`);
      if (paddingBottom > 0) paddings.push(`↓${paddingBottom}px`);
      if (paddingLeft > 0) paddings.push(`←${paddingLeft}px`);
      labelText += ` | padding: ${paddings.join(' ')}`;
    }

    // Add margin info if margin exists
    if (hasMargin) {
      const margins = [];
      if (marginTop > 0) margins.push(`↑${marginTop}px`);
      if (marginRight > 0) margins.push(`→${marginRight}px`);
      if (marginBottom > 0) margins.push(`↓${marginBottom}px`);
      if (marginLeft > 0) margins.push(`←${marginLeft}px`);
      labelText += ` | margin: ${margins.join(' ')}`;
    }

    this.label.textContent = labelText;

    // Position label at top-right of highlight
    const labelX = rect.right - this.label.offsetWidth;
    const labelY = rect.top - this.label.offsetHeight - 4;

    // Keep label on screen
    const finalX = Math.max(4, Math.min(labelX, window.innerWidth - this.label.offsetWidth - 4));
    const finalY = Math.max(4, labelY < 0 ? rect.bottom + 4 : labelY);

    this.label.style.left = `${finalX}px`;
    this.label.style.top = `${finalY}px`;
  }

  private async handleClick(e: MouseEvent) {
    if (!this.isActive) return;

    e.preventDefault();
    e.stopPropagation();

    // Copy the currently highlighted element immediately
    const elementToCopy = this.parentChain[this.currentParentIndex] || this.currentTarget;
    if (!elementToCopy) return;

    const selector = this.generateSelector(elementToCopy);
    await this.copyToClipboard(selector, elementToCopy);

    // Deactivate silently so the "copied" message isn't overwritten
    this.deactivate(true);
  }

  private generateSelector(element: HTMLElement): string {
    // Priority: ID > Custom tag name > Class + tag > Tag with nth-child

    if (element.id) {
      return `#${element.id}`;
    }

    // For custom elements (web components)
    if (element.tagName.includes('-')) {
      return element.tagName.toLowerCase();
    }

    // Class-based selector
    if (element.className && typeof element.className === 'string') {
      const classes = element.className.trim().split(/\s+/).filter(c => c);
      if (classes.length > 0) {
        return `${element.tagName.toLowerCase()}.${classes[0]}`;
      }
    }

    // Tag with nth-child
    const parent = element.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter(
        child => child.tagName === element.tagName
      );
      if (siblings.length > 1) {
        const index = siblings.indexOf(element) + 1;
        return `${element.tagName.toLowerCase()}:nth-child(${index})`;
      }
    }

    return element.tagName.toLowerCase();
  }

  private async copyToClipboard(selector: string, element: HTMLElement) {
    console.log('=== COPY TO CLIPBOARD CALLED ===');
    console.log('Element:', element.tagName, element);
    console.log('Navigator clipboard available:', !!navigator.clipboard);
    console.log('Clipboard writeText available:', !!navigator.clipboard?.writeText);

    try {
      // Get ALL attributes
      const attributes: Record<string, string> = {};
      if (element.attributes) {
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          attributes[attr.name] = attr.value;
        }
      }

      // Build parent hierarchy (including shadow DOM)
      console.log('Starting hierarchy build...');
      const hierarchy: string[] = [];
      let current: HTMLElement | null = element;
      let rootComponent = 'unknown';
      let iterations = 0;

      while (current && current !== document.body && current !== document.documentElement) {
        iterations++;
        console.log(`Iteration ${iterations}: Processing`, current.tagName);
        const tag = current.tagName.toLowerCase();
        const id = current.id ? `#${current.id}` : '';
        const cls = current.className && typeof current.className === 'string'
          ? `.${current.className.trim().split(/\s+/)[0]}`
          : '';

        hierarchy.push(`${tag}${id}${cls}`);

        // Track root component (custom element)
        if (tag.includes('-')) {
          rootComponent = tag;
        }

        // Try to get parent element
        let parent: HTMLElement | null = current.parentElement;

        // If no parent element, check if we're in a shadow root
        if (!parent) {
          const parentNode = current.parentNode;
          console.log('No parent element for', tag, 'parentNode:', parentNode, 'has host?', (parentNode as any)?.host);

          if (parentNode && (parentNode as any).host) {
            // We're at the top of a shadow tree, jump to host
            hierarchy.push('↑[shadow-root]');
            current = (parentNode as ShadowRoot).host as HTMLElement;
            console.log('Jumped to shadow host:', current.tagName);
            continue;
          } else {
            // No parent and no shadow root, we're done
            console.log('Breaking - no parent and no shadow host');
            break;
          }
        }

        current = parent;
      }

      hierarchy.reverse();
      const hierarchyPath = hierarchy.join(' > ');

      console.log('Final hierarchy:', hierarchyPath);
      console.log('Root component:', rootComponent);

      // Detect page/component context
      let pageContext = 'Unknown Page';
      if (rootComponent.includes('app-home')) pageContext = 'Home Page (app-home)';
      else if (rootComponent.includes('app-brainstorm')) pageContext = 'Brainstorm Page (app-brainstorm)';
      else if (rootComponent.includes('app-about')) pageContext = 'About Page (app-about)';
      else if (rootComponent.includes('app-nav')) pageContext = 'Navigation Component (app-nav)';
      else if (rootComponent.includes('app-index')) pageContext = 'Root App (app-index)';

      // Get element info for better context
      const elementInfo = {
        selector,
        tagName: element.tagName.toLowerCase(),
        id: element.id || null,
        classes: element.className && typeof element.className === 'string'
          ? element.className.trim().split(/\s+/).filter(c => c)
          : [],
        attributes,
        textContent: element.textContent?.trim().substring(0, 100) || null,
        outerHTML: element.outerHTML.substring(0, 300)
      };

      // Build attribute string
      const attrStrings: string[] = [];
      for (const [key, value] of Object.entries(attributes)) {
        if (key !== 'class' && key !== 'id') {
          attrStrings.push(`${key}="${value}"`);
        }
      }

      const copyText = `=== ELEMENT INSPECTOR ===

LOCATION:
Page/Component: ${pageContext}
Root Component: ${rootComponent}

HIERARCHY:
${hierarchyPath}

ELEMENT DETAILS:
Selector: ${selector}
Tag: ${elementInfo.tagName}
${elementInfo.id ? `ID: ${elementInfo.id}\n` : ''}${elementInfo.classes.length > 0 ? `Classes: ${elementInfo.classes.join(', ')}\n` : ''}${attrStrings.length > 0 ? `Attributes: ${attrStrings.join(', ')}\n` : ''}${elementInfo.textContent ? `Text: ${elementInfo.textContent}${element.textContent && element.textContent.length > 100 ? '...' : ''}\n` : ''}
HTML PREVIEW:
${elementInfo.outerHTML}${element.outerHTML.length > 300 ? '...' : ''}`;

      console.log('Attempting to copy text to clipboard...');
      console.log('Copy text length:', copyText.length);

      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(copyText);
        console.log('✓ Copied using navigator.clipboard.writeText');
        this.showNotification(`✓ تم النسخ بنجاح: ${selector}`, false, 3000);
      } else {
        // Fallback to execCommand for older browsers
        console.log('Falling back to execCommand method');
        const textarea = document.createElement('textarea');
        textarea.value = copyText;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);

        if (success) {
          console.log('✓ Copied using execCommand');
          this.showNotification(`✓ تم النسخ بنجاح: ${selector}`, false, 3000);
        } else {
          throw new Error('execCommand copy failed');
        }
      }

      console.log('Element Inspector - Copied:', elementInfo);
      console.log('Full copied text:', copyText);
    } catch (err) {
      console.error('Failed to copy:', err);
      console.error('Error details:', err instanceof Error ? err.message : String(err));
      this.showNotification('✗ فشل النسخ!', true);
    }
  }

  private showNotification(message: string, isError = false, duration = 2500) {
    const notification = document.createElement('div');
    notification.textContent = message;

    // Use primary-blue for success, destructive for errors
    const bgColor = isError ? 'var(--destructive)' : 'var(--primary-blue)';
    const borderColor = isError ? 'var(--destructive)' : 'var(--primary-blue)';
    const textColor = isError ? 'var(--destructive-foreground)' : 'var(--primary-blue-foreground)';

    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 24px;
      background: ${bgColor};
      color: ${textColor};
      font-family: var(--font-mono);
      border: 2px solid ${borderColor};
      z-index: 1000000;
      direction: rtl;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      border-radius: 0px;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.transition = 'opacity 0.3s ease';
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, duration);
  }
}
